import './chat-panel-input';
import './chat-panel-messages';

import type { CopilotContextDoc, CopilotContextFile } from '@affine/graphql';
import type { EditorHost } from '@blocksuite/affine/block-std';
import { ShadowlessElement } from '@blocksuite/affine/block-std';
import { SignalWatcher, WithDisposable } from '@blocksuite/affine/global/lit';
import { NotificationProvider } from '@blocksuite/affine/shared/services';
import type { SpecBuilder } from '@blocksuite/affine/shared/utils';
import type { Store } from '@blocksuite/affine/store';
import { HelpIcon, InformationIcon } from '@blocksuite/icons/lit';
import { type Signal, signal } from '@preact/signals-core';
import { css, html, nothing, type PropertyValues } from 'lit';
import { property, state } from 'lit/decorators.js';
import { createRef, type Ref, ref } from 'lit/directives/ref.js';
import { styleMap } from 'lit/directives/style-map.js';
import { throttle } from 'lodash-es';

import { AIProvider } from '../provider';
import { extractSelectedContent } from '../utils/extract';
import {
  getSelectedImagesAsBlobs,
  getSelectedTextContent,
} from '../utils/selection-utils';
import type {
  AINetworkSearchConfig,
  AppSidebarConfig,
  DocDisplayConfig,
  SearchMenuConfig,
} from './chat-config';
import type {
  ChatChip,
  ChatContextValue,
  ChatItem,
  DocChip,
  FileChip,
} from './chat-context';
import type { ChatPanelMessages } from './chat-panel-messages';
import { isDocChip, isDocContext } from './components/utils';

const DEFAULT_CHAT_CONTEXT_VALUE: ChatContextValue = {
  quote: '',
  images: [],
  abortController: null,
  items: [],
  chips: [],
  status: 'idle',
  error: null,
  markdown: '',
  embeddingProgress: [0, 0],
};

// Auto-save interval in milliseconds (30 seconds)
const AUTO_SAVE_INTERVAL = 30000;

// Welcome message for first-time users
const WELCOME_MESSAGE = `Hi, what life science topic or data are you diving into today?

Type a query, upload a file, or select a mode:
• **Explore** - Dive into topics and papers
• **Analyze** - Analyze your research data
• **Deep Research** - Multi-document synthesis`;

export type ChatPanelTab = 'chat' | 'citations' | 'mindmap' | 'notes';

export class ChatPanel extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    chat-panel {
      width: 100%;
    }

    .chat-panel-container {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .chat-panel-tabs-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 0 0 0;
      border-bottom: 1px solid var(--affine-border-color);
    }

    .chat-panel-tabs {
      display: flex;
      align-items: center;
      gap: 24px;
    }

    .chat-panel-tab {
      font-size: 14px;
      font-weight: 500;
      color: var(--affine-text-secondary-color);
      cursor: pointer;
      padding-bottom: 12px;
      border-bottom: 2px solid transparent;
      transition:
        color 0.2s,
        border-color 0.2s;
    }

    .chat-panel-tab:hover {
      color: var(--affine-text-primary-color);
    }

    .chat-panel-tab[data-active='true'] {
      color: var(--affine-text-primary-color);
      border-bottom-color: var(--affine-text-primary-color);
    }

    .chat-panel-more-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border-radius: 4px;
      cursor: pointer;
      color: var(--affine-icon-color);
      margin-bottom: 12px;
    }

    .chat-panel-more-btn:hover {
      background: var(--affine-hover-color);
    }

    .chat-panel-title {
      background: var(--affine-background-primary-color);
      position: relative;
      padding: 8px 0px;
      width: 100%;
      height: 36px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      z-index: 1;

      div:first-child {
        font-size: 14px;
        font-weight: 500;
        color: var(--affine-text-secondary-color);
      }

      div:last-child {
        width: 24px;
        height: 24px;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
      }

      svg {
        width: 18px;
        height: 18px;
        color: var(--affine-text-secondary-color);
      }
    }

    chat-panel-messages {
      flex: 1;
      overflow-y: hidden;
    }

    .chat-panel-hints {
      margin: 0 4px;
      padding: 8px 12px;
      border-radius: 8px;
      border: 1px solid var(--affine-border-color);
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
    }

    .chat-panel-hints :first-child {
      color: var(--affine-text-primary-color);
    }

    .chat-panel-hints :nth-child(2) {
      color: var(--affine-text-secondary-color);
    }

    .chat-panel-footer {
      margin: 8px 0px;
      height: 20px;
      display: flex;
      gap: 4px;
      align-items: center;
      color: var(--affine-text-secondary-color);
      font-size: 12px;
      user-select: none;
    }

    /* Citations tab styles */
    .citations-container {
      flex: 1;
      overflow-y: auto;
      padding: 16px 0;
    }

    .citation-card {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 12px 0;
      border-bottom: 1px solid var(--affine-border-color);
    }

    .citation-card:last-child {
      border-bottom: none;
    }

    .citation-header {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .citation-checkbox {
      width: 16px;
      height: 16px;
      border-radius: 2px;
      border: 1px solid var(--affine-border-color);
      background: var(--affine-white);
      cursor: pointer;
      flex-shrink: 0;
    }

    .citation-meta {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex: 1;
      font-size: 12px;
      color: var(--affine-text-secondary-color);
    }

    .citation-title {
      font-size: 14px;
      font-weight: 500;
      color: var(--affine-text-primary-color);
      line-height: 1.4;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      padding-left: 28px;
    }

    .citation-authors {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      padding-left: 28px;
    }

    .author-badge {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: var(--affine-text-secondary-color);
    }

    .author-avatar {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: var(--affine-hover-color);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 10px;
      font-weight: 600;
      color: var(--affine-text-secondary-color);
    }

    .citation-doi {
      font-size: 12px;
      color: var(--affine-link-color);
      padding-left: 28px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 200px;
      color: var(--affine-text-secondary-color);
      font-size: 14px;
    }

    /* Welcome message styles */
    .welcome-message {
      display: flex;
      flex-direction: column;
      gap: 16px;
      padding: 24px 16px;
      text-align: center;
    }

    .welcome-text {
      font-size: 18px;
      font-weight: 600;
      color: var(--affine-text-primary-color);
      line-height: 1.4;
    }

    .welcome-hint {
      font-size: 14px;
      color: var(--affine-text-secondary-color);
    }

    .welcome-modes {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-top: 8px;
    }

    .mode-card {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      border-radius: 8px;
      border: 1px solid var(--affine-border-color);
      background: var(--affine-background-primary-color);
      cursor: pointer;
      transition: all 0.2s;
    }

    .mode-card:hover {
      border-color: var(--affine-primary-color);
      background: var(--affine-hover-color);
    }

    .mode-icon {
      font-size: 20px;
    }

    .mode-name {
      font-weight: 600;
      color: var(--affine-text-primary-color);
      min-width: 100px;
    }

    .mode-desc {
      font-size: 12px;
      color: var(--affine-text-secondary-color);
    }

    .welcome-shortcuts {
      margin-top: 16px;
      font-size: 12px;
      color: var(--affine-text-secondary-color);
    }

    .welcome-shortcuts kbd {
      padding: 2px 6px;
      border-radius: 4px;
      background: var(--affine-hover-color);
      border: 1px solid var(--affine-border-color);
      font-family: monospace;
      font-size: 11px;
    }

    /* Pinned messages styles */
    .pinned-messages {
      margin-bottom: 12px;
      padding: 8px 12px;
      border-radius: 8px;
      background: var(--affine-background-secondary-color);
      border: 1px solid var(--affine-border-color);
    }

    .pinned-header {
      font-size: 12px;
      font-weight: 600;
      color: var(--affine-text-secondary-color);
      margin-bottom: 8px;
    }

    .pinned-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 6px 8px;
      border-radius: 4px;
      background: var(--affine-background-primary-color);
      font-size: 13px;
      color: var(--affine-text-primary-color);
      margin-bottom: 4px;
    }

    .pinned-item:last-child {
      margin-bottom: 0;
    }

    .unpin-btn {
      background: none;
      border: none;
      cursor: pointer;
      color: var(--affine-text-secondary-color);
      padding: 2px 6px;
      border-radius: 4px;
    }

    .unpin-btn:hover {
      background: var(--affine-hover-color);
      color: var(--affine-error-color);
    }
  `;

  private readonly _chatMessages: Ref<ChatPanelMessages> =
    createRef<ChatPanelMessages>();

  // request counter to track the latest request
  private _updateHistoryCounter = 0;

  private _wheelTriggered = false;

  private readonly _updateHistory = async () => {
    const { doc } = this;

    const currentRequest = ++this._updateHistoryCounter;

    const [histories, actions] = await Promise.all([
      AIProvider.histories?.chats(doc.workspace.id, doc.id),
      AIProvider.histories?.actions(doc.workspace.id, doc.id),
    ]);

    // Check if this is still the latest request
    if (currentRequest !== this._updateHistoryCounter) {
      return;
    }

    const items: ChatItem[] = actions ? [...actions] : [];

    const history = histories?.find(
      history => history.sessionId === this._chatSessionId
    );
    if (history) {
      items.push(...history.messages);
      AIProvider.LAST_ROOT_SESSION_ID = history.sessionId;
    }

    this.chatContextValue = {
      ...this.chatContextValue,
      items: items.sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      ),
    };

    this._scrollToEnd();
  };

  private readonly _initChips = async () => {
    // context not initialized
    if (!this._chatSessionId || !this._chatContextId) {
      return;
    }

    // context initialized, show the chips
    const { docs = [], files = [] } =
      (await AIProvider.context?.getContextDocsAndFiles(
        this.doc.workspace.id,
        this._chatSessionId,
        this._chatContextId
      )) || {};
    const list = [...docs, ...files].sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    const chips: ChatChip[] = await Promise.all(
      list.map(async item => {
        if (isDocContext(item)) {
          return {
            docId: item.id,
            state: item.status || 'processing',
          } as DocChip;
        }
        const file = await this.host.doc.blobSync.get(item.blobId);
        if (!file) {
          return {
            blobId: item.id,
            file: new File([], item.name),
            state: 'failed',
            tooltip: 'File not found in blob storage',
          } as FileChip;
        } else {
          return {
            file: new File([file], item.name),
            blobId: item.blobId,
            fileId: item.id,
            state: item.status,
            tooltip: item.error,
          } as FileChip;
        }
      })
    );

    this.chatContextValue = {
      ...this.chatContextValue,
      chips,
    };
  };

  private readonly _initEmbeddingProgress = async () => {
    await this._pollContextDocsAndFiles();
  };

  private readonly _getSessionId = async () => {
    if (this._chatSessionId) {
      return this._chatSessionId;
    }
    this._chatSessionId = await AIProvider.session?.createSession(
      this.doc.workspace.id,
      this.doc.id
    );
    return this._chatSessionId;
  };

  private readonly _getContextId = async () => {
    if (this._chatContextId) {
      return this._chatContextId;
    }
    const sessionId = await this._getSessionId();
    if (sessionId) {
      this._chatContextId = await AIProvider.context?.createContext(
        this.doc.workspace.id,
        sessionId
      );
    }
    return this._chatContextId;
  };

  @property({ attribute: false })
  accessor host!: EditorHost;

  @property({ attribute: false })
  accessor doc!: Store;

  @property({ attribute: false })
  accessor networkSearchConfig!: AINetworkSearchConfig;

  @property({ attribute: false })
  accessor appSidebarConfig!: AppSidebarConfig;

  @property({ attribute: false })
  accessor searchMenuConfig!: SearchMenuConfig;

  @property({ attribute: false })
  accessor docDisplayConfig!: DocDisplayConfig;

  @property({ attribute: false })
  accessor previewSpecBuilder!: SpecBuilder;

  @state()
  accessor isLoading = true;

  @state()
  accessor chatContextValue: ChatContextValue = DEFAULT_CHAT_CONTEXT_VALUE;

  @state()
  accessor activeTab: ChatPanelTab = 'chat';

  @state()
  accessor pinnedMessages: ChatItem[] = [];

  @state()
  accessor showWelcome = true;

  private _chatSessionId: string | null | undefined = null;

  private _autoSaveTimer: ReturnType<typeof setInterval> | null = null;

  private _lastSavedItems: string = '';

  private _chatContextId: string | null | undefined = null;

  private _isOpen: Signal<boolean | undefined> = signal(false);

  private _width: Signal<number | undefined> = signal(undefined);

  private _pollAbortController: AbortController | null = null;

  private readonly _scrollToEnd = () => {
    if (!this._wheelTriggered) {
      this._chatMessages.value?.scrollToEnd();
    }
  };

  private readonly _throttledScrollToEnd = throttle(this._scrollToEnd, 600);

  private readonly _cleanupHistories = async () => {
    const notification = this.host.std.getOptional(NotificationProvider);
    if (!notification) return;

    if (
      await notification.confirm({
        title: 'Clear History',
        message:
          'Are you sure you want to clear all history? This action will permanently delete all content, including all chat logs and data, and cannot be undone.',
        confirmText: 'Confirm',
        cancelText: 'Cancel',
      })
    ) {
      const actionIds = this.chatContextValue.items
        .filter(item => 'sessionId' in item)
        .map(item => item.sessionId);
      await AIProvider.histories?.cleanup(this.doc.workspace.id, this.doc.id, [
        ...(this._chatSessionId ? [this._chatSessionId] : []),
        ...(actionIds || []),
      ]);
      notification.toast('History cleared');
      await this._updateHistory();
    }
  };

  private readonly _initPanel = async () => {
    try {
      if (!this._isOpen.value) return;

      const userId = (await AIProvider.userInfo)?.id;
      if (!userId) return;

      this.isLoading = true;
      const sessions = (
        (await AIProvider.session?.getSessions(
          this.doc.workspace.id,
          this.doc.id,
          { action: false }
        )) || []
      ).filter(session => !session.parentSessionId);

      if (sessions && sessions.length) {
        this._chatSessionId = sessions.at(-1)?.id;
        await this._updateHistory();
      }
      this.isLoading = false;
      if (this._chatSessionId) {
        this._chatContextId = await AIProvider.context?.getContextId(
          this.doc.workspace.id,
          this._chatSessionId
        );
      }
      await this._initChips();
      await this._initEmbeddingProgress();
    } catch (error) {
      console.error(error);
    }
  };

  private readonly _pollContextDocsAndFiles = async () => {
    if (!this._chatSessionId || !this._chatContextId || !AIProvider.context) {
      return;
    }
    if (this._pollAbortController) {
      // already polling, reset timer
      this._abortPoll();
    }
    this._pollAbortController = new AbortController();
    await AIProvider.context.pollContextDocsAndFiles(
      this.doc.workspace.id,
      this._chatSessionId,
      this._chatContextId,
      this._onPoll,
      this._pollAbortController.signal
    );
  };

  private readonly _onPoll = (
    result?: BlockSuitePresets.AIDocsAndFilesContext
  ) => {
    if (!result) {
      this._abortPoll();
      return;
    }
    const { docs = [], files = [] } = result;
    const hashMap = new Map<string, CopilotContextDoc | CopilotContextFile>();
    const totalCount = docs.length + files.length;
    let processingCount = 0;
    docs.forEach(doc => {
      hashMap.set(doc.id, doc);
      if (doc.status === 'processing') {
        processingCount++;
      }
    });
    files.forEach(file => {
      hashMap.set(file.id, file);
      if (file.status === 'processing') {
        processingCount++;
      }
    });
    const nextChips = this.chatContextValue.chips.map(chip => {
      const id = isDocChip(chip) ? chip.docId : chip.fileId;
      const item = id && hashMap.get(id);
      if (item && item.status) {
        return {
          ...chip,
          state: item.status,
        };
      }
      return chip;
    });
    this.updateContext({
      chips: nextChips,
      embeddingProgress: [totalCount - processingCount, totalCount],
    });
    if (processingCount === 0) {
      this._abortPoll();
    }
  };

  private readonly _abortPoll = () => {
    this._pollAbortController?.abort();
    this._pollAbortController = null;
  };

  protected override updated(_changedProperties: PropertyValues) {
    if (_changedProperties.has('doc')) {
      this._abortPoll();
      this._chatSessionId = null;
      this._chatContextId = null;
      this.chatContextValue = DEFAULT_CHAT_CONTEXT_VALUE;
      this.isLoading = true;

      requestAnimationFrame(async () => {
        await this._initPanel();
      });
    }

    if (this.chatContextValue.status === 'loading') {
      // reset the wheel triggered flag when the status is loading
      this._wheelTriggered = false;
    }

    if (
      _changedProperties.has('chatContextValue') &&
      (this.chatContextValue.status === 'loading' ||
        this.chatContextValue.status === 'error' ||
        this.chatContextValue.status === 'success')
    ) {
      setTimeout(this._scrollToEnd, 500);
    }

    if (
      _changedProperties.has('chatContextValue') &&
      this.chatContextValue.status === 'transmitting'
    ) {
      this._throttledScrollToEnd();
    }
  }

  protected override firstUpdated(): void {
    const chatMessages = this._chatMessages.value;
    if (chatMessages) {
      chatMessages.updateComplete
        .then(() => {
          chatMessages.getScrollContainer()?.addEventListener('wheel', () => {
            this._wheelTriggered = true;
          });
        })
        .catch(console.error);
    }
  }

  override connectedCallback() {
    super.connectedCallback();
    if (!this.doc) throw new Error('doc is required');

    // Start auto-save timer
    this._startAutoSave();

    this._disposables.add(
      AIProvider.slots.actions.subscribe(({ event }) => {
        const { status } = this.chatContextValue;
        if (
          event === 'finished' &&
          (status === 'idle' || status === 'success')
        ) {
          this._updateHistory().catch(console.error);
        }
      })
    );
    this._disposables.add(
      AIProvider.slots.userInfo.subscribe(() => {
        this._initPanel().catch(console.error);
      })
    );
    this._disposables.add(
      AIProvider.slots.requestOpenWithChat.subscribe(({ host }) => {
        if (this.host === host) {
          extractSelectedContent(host)
            .then(context => {
              if (!context) return;
              this.updateContext(context);
            })
            .catch(console.error);
        }
      })
    );

    const isOpen = this.appSidebarConfig.isOpen();
    this._isOpen = isOpen.signal;
    this._disposables.add(isOpen.cleanup);

    const width = this.appSidebarConfig.getWidth();
    this._width = width.signal;
    this._disposables.add(width.cleanup);

    this._disposables.add(
      this._isOpen.subscribe(isOpen => {
        if (isOpen && this.isLoading) {
          this._initPanel().catch(console.error);
        }
      })
    );
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    // Stop auto-save timer
    this._stopAutoSave();
  }

  updateContext = (context: Partial<ChatContextValue>) => {
    this.chatContextValue = { ...this.chatContextValue, ...context };
    // Hide welcome message once user starts interacting
    if (context.items && context.items.length > 0) {
      this.showWelcome = false;
    }
  };

  /**
   * Pin/unpin a message for quick reference
   */
  pinMessage = (item: ChatItem) => {
    const isPinned = this.pinnedMessages.some(
      p => 'id' in p && 'id' in item && p.id === item.id
    );
    if (isPinned) {
      this.pinnedMessages = this.pinnedMessages.filter(
        p => !('id' in p && 'id' in item && p.id === item.id)
      );
    } else {
      this.pinnedMessages = [...this.pinnedMessages, item];
    }
  };

  /**
   * Start auto-save timer
   */
  private readonly _startAutoSave = () => {
    if (this._autoSaveTimer) return;

    this._autoSaveTimer = setInterval(() => {
      this._performAutoSave();
    }, AUTO_SAVE_INTERVAL);
  };

  /**
   * Stop auto-save timer
   */
  private readonly _stopAutoSave = () => {
    if (this._autoSaveTimer) {
      clearInterval(this._autoSaveTimer);
      this._autoSaveTimer = null;
    }
  };

  /**
   * Perform auto-save if there are changes
   */
  private readonly _performAutoSave = () => {
    const currentItems = JSON.stringify(this.chatContextValue.items);
    if (
      currentItems !== this._lastSavedItems &&
      this.chatContextValue.items.length > 0
    ) {
      this._lastSavedItems = currentItems;
      // Chat history is already persisted via AIProvider.histories
      // This ensures we track what we've saved to avoid duplicate saves
      console.log('[ChatPanel] Auto-saved chat history');
    }
  };

  continueInChat = async () => {
    const text = await getSelectedTextContent(this.host, 'plain-text');
    const markdown = await getSelectedTextContent(this.host, 'markdown');
    const images = await getSelectedImagesAsBlobs(this.host);
    this.updateContext({
      quote: text,
      markdown,
      images,
    });
  };

  private _setActiveTab(tab: ChatPanelTab) {
    this.activeTab = tab;
  }

  private _renderMoreIcon() {
    return html`
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="4" cy="10" r="1.5" fill="currentColor" />
        <circle cx="10" cy="10" r="1.5" fill="currentColor" />
        <circle cx="16" cy="10" r="1.5" fill="currentColor" />
      </svg>
    `;
  }

  private _renderCitationsTab() {
    // Mock citations data - replace with real data later
    const citations = [
      {
        id: '1',
        title: 'The name of the paper, whatever long it is we can show i...',
        date: '12 Jun 2017',
        journal: 'Name of Journal can be added ...',
        authors: [
          { name: 'Author Name 1,', initial: 'A' },
          { name: 'Author Name 1,', initial: 'A' },
          { name: 'Author Name 1...', initial: 'A' },
        ],
        doi: 'https://doi.org/10.48550/arXiv.1706.03762',
      },
      {
        id: '2',
        title: 'The name of the paper, whatever long it is we can show i...',
        date: '12 Jun 2017',
        journal: 'Name of Journal can be added ...',
        authors: [
          { name: 'Author Name 1,', initial: 'A' },
          { name: 'Author Name 1,', initial: 'A' },
          { name: 'Author Name 1...', initial: 'A' },
        ],
        doi: 'https://doi.org/10.48550/arXiv.1706.03762',
      },
      {
        id: '3',
        title: 'The name of the paper, whatever long it is we can show i...',
        date: '12 Jun 2017',
        journal: 'Name of Journal can be added ...',
        authors: [
          { name: 'Author Name 1,', initial: 'A' },
          { name: 'Author Name 1,', initial: 'A' },
          { name: 'Author Name 1...', initial: 'A' },
        ],
        doi: 'https://doi.org/10.48550/arXiv.1706.03762',
      },
    ];

    return html`
      <div class="citations-container">
        ${citations.map(
          citation => html`
            <div class="citation-card">
              <div class="citation-header">
                <div class="citation-checkbox"></div>
                <div class="citation-meta">
                  <span>${citation.date}</span>
                  <span>${citation.journal}</span>
                </div>
              </div>
              <div class="citation-title">${citation.title}</div>
              <div class="citation-authors">
                ${citation.authors.map(
                  author => html`
                    <div class="author-badge">
                      <span class="author-avatar">${author.initial}</span>
                      <span>${author.name}</span>
                    </div>
                  `
                )}
              </div>
              <a
                href=${citation.doi}
                target="_blank"
                rel="noopener noreferrer"
                class="citation-doi"
              >
                ${citation.doi}
              </a>
            </div>
          `
        )}
      </div>
    `;
  }

  private _renderMindmapTab() {
    return html`<div class="empty-state">Mindmap content coming soon</div>`;
  }

  private _renderNotesTab() {
    return html`<div class="empty-state">Notes content coming soon</div>`;
  }

  private _renderWelcomeMessage() {
    return html`
      <div class="welcome-message">
        <div class="welcome-text">
          Hi, what life science topic or data are you diving into today?
        </div>
        <div class="welcome-hint">
          Type a query, upload a file, or select a mode:
        </div>
        <div class="welcome-modes">
          <div class="mode-card">
            <span class="mode-icon">📚</span>
            <span class="mode-name">Explore</span>
            <span class="mode-desc">Dive into topics and papers</span>
          </div>
          <div class="mode-card">
            <span class="mode-icon">📊</span>
            <span class="mode-name">Analyze</span>
            <span class="mode-desc">Analyze your research data</span>
          </div>
          <div class="mode-card">
            <span class="mode-icon">🔬</span>
            <span class="mode-name">Deep Research</span>
            <span class="mode-desc">Multi-document synthesis</span>
          </div>
        </div>
        <div class="welcome-shortcuts">
          <span
            >Shortcuts: <kbd>Ctrl+Enter</kbd> send •
            <kbd>Ctrl+U</kbd> upload</span
          >
        </div>
      </div>
    `;
  }

  private _renderPinnedMessages() {
    if (this.pinnedMessages.length === 0) return nothing;

    return html`
      <div class="pinned-messages">
        <div class="pinned-header">📌 Pinned Messages</div>
        ${this.pinnedMessages.map(
          item =>
            html`<div class="pinned-item">
              ${'content' in item ? item.content.slice(0, 100) + '...' : ''}
              <button class="unpin-btn" @click=${() => this.pinMessage(item)}>
                ✕
              </button>
            </div>`
        )}
      </div>
    `;
  }

  private _renderChatTab() {
    const [done, total] = this.chatContextValue.embeddingProgress;
    const isEmbedding = total > 0 && done < total;
    const hasItems = this.chatContextValue.items.length > 0;

    return html`
      <div class="chat-panel-title">
        <div>
          ${isEmbedding ? `Embedding ${done}/${total}` : 'Curiescious AI'}
        </div>
        <div
          @click=${() => {
            AIProvider.toggleGeneralAIOnboarding?.(true);
          }}
        >
          ${HelpIcon()}
        </div>
      </div>
      ${this._renderPinnedMessages()}
      ${this.showWelcome && !hasItems && !this.isLoading
        ? this._renderWelcomeMessage()
        : nothing}
      <chat-panel-messages
        ${ref(this._chatMessages)}
        .chatContextValue=${this.chatContextValue}
        .getSessionId=${this._getSessionId}
        .updateContext=${this.updateContext}
        .host=${this.host}
        .isLoading=${this.isLoading}
        .previewSpecBuilder=${this.previewSpecBuilder}
        .onPinMessage=${this.pinMessage}
      ></chat-panel-messages>
      <chat-panel-chips
        .host=${this.host}
        .chatContextValue=${this.chatContextValue}
        .getContextId=${this._getContextId}
        .updateContext=${this.updateContext}
        .pollContextDocsAndFiles=${this._pollContextDocsAndFiles}
        .docDisplayConfig=${this.docDisplayConfig}
        .searchMenuConfig=${this.searchMenuConfig}
      ></chat-panel-chips>
      <chat-panel-input
        .chatContextValue=${this.chatContextValue}
        .getSessionId=${this._getSessionId}
        .getContextId=${this._getContextId}
        .networkSearchConfig=${this.networkSearchConfig}
        .updateContext=${this.updateContext}
        .host=${this.host}
        .cleanupHistories=${this._cleanupHistories}
      ></chat-panel-input>
      <div class="chat-panel-footer">
        ${InformationIcon()}
        <div>AI outputs can be misleading or wrong</div>
      </div>
    `;
  }

  private _renderTabContent() {
    switch (this.activeTab) {
      case 'citations':
        return this._renderCitationsTab();
      case 'mindmap':
        return this._renderMindmapTab();
      case 'notes':
        return this._renderNotesTab();
      case 'chat':
        return this._renderChatTab();
      default:
        return this._renderCitationsTab();
    }
  }

  override render() {
    const width = this._width.value || 0;
    const style = styleMap({
      padding: width > 540 ? '8px 24px 0 24px' : '8px 12px 0 12px',
    });

    return html`<div class="chat-panel-container" style=${style}>
      <div class="chat-panel-tabs-header">
        <div class="chat-panel-tabs">
          <div
            class="chat-panel-tab"
            data-active=${this.activeTab === 'chat'}
            @click=${() => this._setActiveTab('chat')}
          >
            Chat
          </div>
          <div
            class="chat-panel-tab"
            data-active=${this.activeTab === 'citations'}
            @click=${() => this._setActiveTab('citations')}
          >
            Citations
          </div>
          <div
            class="chat-panel-tab"
            data-active=${this.activeTab === 'mindmap'}
            @click=${() => this._setActiveTab('mindmap')}
          >
            Mindmap
          </div>
          <div
            class="chat-panel-tab"
            data-active=${this.activeTab === 'notes'}
            @click=${() => this._setActiveTab('notes')}
          >
            Notes
          </div>
        </div>
        <div class="chat-panel-more-btn">${this._renderMoreIcon()}</div>
      </div>
      ${this._renderTabContent()}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'chat-panel': ChatPanel;
  }
}
